/* eslint-disable no-undef */

import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";

import Logger from './Logger';
import { throttleLogger } from './utils';

const logger = new Logger('VirtualReality');

/**
 * 为视频添加虚拟背景类
 */
export default class VirtualReality {

    constructor() {

        this._controller = null;
        this._timestamp = null;
        this._videoStream = null;
        this._customBackgroundImage = new Image();

        this._customBackgroundImage.src = `${window.app_base_path}/images/virtualBg/tainan.jpeg`;  // 默认背景图

        this._canvasElement = null;
        this._canvasCtx = null;

        this._isMirror = false;
        this._isBlur = false;
        this._isVBg = false; // 是否设置了虚拟背景

        this._selfieSegmentation = new SelfieSegmentation({
            locateFile: (file) => {
                return `/assets/${file}`;
            }
        });

        this._selfieSegmentation.setOptions({
            modelSelection: 1
        });

        this._selfieSegmentation.onResults((results) => {

            throttleLogger(`当前状态值 [ _isMirror: ${this._isMirror} ] [ _isBlur: ${this._isBlur} ] [ _isVBg: ${this._isVBg} ] `, 5 * 1000);

            const { width: canvasWidth, height: canvasHeight } = this._canvasElement;

            this._canvasCtx.save();
            this._canvasCtx.clearRect(0, 0, canvasWidth, canvasHeight);
            if (this._isMirror) {
                //位移来做镜像翻转(这里也可以实现动画效果)
                this._canvasCtx.translate(canvasWidth / 2 + canvasWidth / 2, 0);
                this._canvasCtx.scale(-1, 1);
            }
            // 绘制原始帧
            this._canvasCtx.drawImage(results.image, 0, 0, canvasWidth, canvasHeight);
            // 使分割遮罩中不存在的所有像素透明
            this._canvasCtx.globalCompositeOperation = 'destination-atop';
            this._canvasCtx.drawImage(results.segmentationMask, 0, 0, canvasWidth, canvasHeight);
            if (this._isBlur) {
                // 模糊所有后续绘制的上下文，然后将原始图像设置为背景
                this._canvasCtx.filter = 'blur(16px)';
            }
            this._canvasCtx.globalCompositeOperation = 'destination-over';

            if (this._isVBg) {
                this._canvasCtx.drawImage(this._customBackgroundImage, 0, 0, canvasWidth, canvasHeight);
            } else {
                this._canvasCtx.drawImage(results.image, 0, 0, canvasWidth, canvasHeight);
            }
            if (this._isMirror) {
                // 坐标参考还原
                this._canvasCtx.setTransform(1, 0, 0, 1, 0, 0);
            }
            this._canvasCtx.restore();

            // new VideoFrame(this._canvasElement, { timestamp: this._timestamp, alpha: 'discard' })
            const _frameFromCanvas = new VideoFrame(this._canvasElement, { timestamp: this._timestamp });

            this._controller.enqueue(_frameFromCanvas);

            // logger.debug(`处理后当前时间: ${this._timestamp} -> ${Date.now()}`)
        });
    }

    get videoStream() {
        return this._videoStream;
    }
    set videoStream(value) {
        this._videoStream = value;
    }

    get isMirror() {
        return this._isMirror;
    }
    set isMirror(flag) {
        this._isMirror = flag;
    }

    get isBlur() {
        return this._isBlur;
    }
    set isBlur(flag) {
        this._isBlur = flag;
    }

    get isVBg() {
        return this._isVBg;
    }
    set isVBg(flag) {
        this._isVBg = flag;
    }

    /**
     * 给 this._videoStream 视频流添加背景
     * @returns
     */
    async transformMediaStream(videoStream, imageUrl) {

        if (videoStream) {
            this._videoStream = videoStream;
        }
        if (!this._videoStream || !this._videoStream.active) {
            await this.openStream();
        }
        if (imageUrl) {
            this._customBackgroundImage.src = imageUrl;
        }

        const videoTrack = this._videoStream.getVideoTracks()[0];
        const trackProcessor = new MediaStreamTrackProcessor({ track: videoTrack });
        const trackGenerator = new MediaStreamTrackGenerator({ kind: 'video' });

        const { width, height } = videoTrack.getSettings();

        this._canvasElement = new OffscreenCanvas(width, height);
        this._canvasCtx = this._canvasElement.getContext('2d');

        const transformer = new TransformStream({

            transform: async (videoFrame, controller) => {

                if (!this._controller) {

                    this._controller = controller;
                }

                this._timestamp = videoFrame.timestamp;

                videoFrame.width = width;
                videoFrame.height = height;

                await this._selfieSegmentation.send({ image: videoFrame });

                videoFrame.close();
                // logger.debug(`处理前当前时间: ${this._timestamp} -> ${Date.now()}`)
                //logger.debug(`将时间戳为 [${this._timestamp}] 的视频帧交给selfieSegmentation 进行转换`);
            }
        });

        trackProcessor.readable.pipeThrough(transformer).pipeTo(trackGenerator.writable).catch(
            err => {
                logger.error('green screen generator err:', err.message)
            });

        const transformedStream = new MediaStream([trackGenerator]);
        return transformedStream;
    }

    /**
     * 更换视频流虚拟背景图
     * @param {*} imageUrl
     */
    changeBackground(imageUrl) {

        if (this._videoStream && this._videoStream.active && this._canvasElement) {

            this._customBackgroundImage.src = imageUrl;

        } else {

            logger.warn(`更换视频流虚拟背景前需要开启视频流转换方法`);
        }
    }

    /**
     * 打开本地视频
     * @returns
     */
    async getLocalStream() {

        if (this._videoStream && this._videoStream.active) {
            this.stopStream();
        }

        await this.openStream();

        return this._videoStream;
    }

    /**
     * 初始化 this._videoStream 视频流对象
     */
    async openStream() {
        try {

            this._videoStream = await navigator.mediaDevices.getUserMedia({
                video: true
            });

        } catch (err) {
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                logger.error('Permission denied error');
            } else {
                logger.error(err.message)
            }
        }
    }

    /**
     * 停止 this._videoStream 视频流对象
     */
    stopStream() {
        this._videoStream.getVideoTracks().map((track) => track.stop());
    }
}
